/**
 * Types describing the root-object schema and validation results returned by the
 * `api/formula` endpoints. These mirror the backend DTOs in
 * `Ezhou.Accounting.Model.Formula` (serialized as camelCase, enums as strings).
 */

/** Well-known formula context keys (mirrors backend `FormulaContextKeys`). */
export const FormulaContextKeys = {
  /** Tessitura transaction formulas (TransactionInfo, AccountingDate, JournalEntry). */
  TessituraContext: "TessituraContext",
} as const;


export type FormulaMemberSchema = {
  name: string;
  /** Friendly type used for tooltips/labels, e.g. "int?", "string", "DateTime?". */
  displayType: string;
  /** Key into {@link FormulaSchema.types} when the member is a complex, drillable type. */
  typeRef?: string | null;
  isNullable: boolean;
  isCollection: boolean;
  /** True when the member has no drillable structure (string, numbers, DateTime, enums, ...). */
  isPrimitive: boolean;
};

export type FormulaTypeSchema = {
  typeRef: string;
  name: string;
  members: FormulaMemberSchema[];
};

export type FormulaRootObject = {
  name: string;
  /** Key into {@link FormulaSchema.types}. Null when the root object is itself a primitive. */
  typeRef?: string | null;
  /** Friendly display type, e.g. "DateTime", "TessituraTransaction". */
  displayType: string;
  /** True when the root object has no drillable structure of its own (e.g. a DateTime). */
  isPrimitive: boolean;
  description?: string | null;
};

export type FormulaSchema = {
  contextKey: string;
  rootObjects: FormulaRootObject[];
  /** Complex types keyed by {@link FormulaTypeSchema.typeRef}. */
  types: Record<string, FormulaTypeSchema>;
};

export type FormulaDiagnosticSeverity = "Info" | "Warning" | "Error";

export type FormulaDiagnostic = {
  severity: FormulaDiagnosticSeverity;
  message: string;
  /** 1-based line, 0 when not available. */
  line: number;
  /** 1-based column, 0 when not available. */
  column: number;
};

export type FormulaValidationResult = {
  isValid: boolean;
  diagnostics: FormulaDiagnostic[];
};
